<template>
    <div :class="['page page--light', { 'page--embed': partnerAppEmbedCustomization }]">
        <section class="row gradient-background">
            <div class="wrapper wrapper--no-max-width">
                <header class="row__header">
                    <back-button :isAbsolute="false"
                        routeToHome
                        :isBlack="true"
                    />

                    <h1>{{ $t('global.transactionHistory') }}</h1>
                </header>

                <transactions-filters v-if="areFiltersVisible"
                    id="transactions-filters"
                    class="filters"
                    :selected-token-code="selectedTokenCode"
                    @filtersRequest="onFiltersRequest"
                    @filtersInteraction="showLoader = true"
                />

                <toggle-options v-else
                    id="transactions-toggle"
                    class="toggle-options"
                    :options.sync="toggleOptions"
                    @toggleOption="setActive"
                />

                <div v-if="transactions.length && !showLoader"
                    class="transactions"
                >
                    <div v-for="(item, index) in transactions"
                        :key="index"
                        :ref="index === openedTransactionIndex ? 'activeTransaction' : ''"
                        :class="['transaction', {'transaction--active': index === openedTransactionIndex}]"
                    >
                        <div v-if="item.showHeading || item.showDate"
                            class="transaction__heading"
                        >
                            <span v-if="item.showHeading"
                                class="heading__title"
                            >{{ item.heading }}</span>

                            <span v-if="item.showDate"
                                class="heading__date"
                            >{{ item.formattedDate }}</span>
                        </div>

                        <div class="transaction__data"
                            @click="makeActive(index)"
                        >
                            <div class="transaction__data-left">
                                <partner-logo class="logo"
                                    :show-placeholder="true"
                                    :url="item.logo.replace('~', '')"
                                />

                                <div class="transaction__name">
                                    {{ item.transactionTypeName }}

                                    <span class="transaction__name--time">
                                        {{ item.formattedStatus }}
                                    </span>
                                </div>
                            </div>

                            <div class="transaction__data-right">
                                <div :class="`transaction__amount transaction__amount--${item.amountColor}`">{{ item.formattedAmount }}</div>
                                <i class="icon-chevron" />
                            </div>
                        </div>

                        <transition @after-enter="afterTransactionDetailsEnter">
                            <wallet-transaction-details v-if="index === openedTransactionIndex"
                                :transaction-id="item.transactionId"
                                class="transaction__details"
                            />
                        </transition>
                    </div>
                </div>

                <div v-if="!transactions.length && !showLoader"
                    class="transactions__empty"
                >
                    {{ $t('transactionHistory.noTransactionsMessage') }}
                </div>

                <pagination v-show="!showLoader && transactions.length"
                    id="transactions-pagination"
                    class="transactions__pagination"
                    :page="transactionsPagination.currentPage"
                    :last-page="transactionsPagination.lastPage"
                    @currentPage="onPaginationUpdate"
                />

                <loading-spinner v-if="showLoader" />
            </div>
        </section>
    </div>
</template>

<script>
// 3rd party
import {
    mapGetters,
    mapActions,
} from 'vuex';

import {
    TransactionType,
} from '@shared/config/transactions-types';

// Components
import toggleOptions from '@webapp/components/status-toggle/status-toggle.vue';
import partnerLogo from '@webapp/components/partner-logo/partner-logo.vue';
import walletTransactionDetails from '@webapp/components/wallet/wallet-transaction-details.vue';
import loadingSpinner from '@webapp/components/loading-spinner/loading-spinner.vue';
import backButton from '@webapp/components/back-button/back-button.vue';
import transactionsFilters from './transactions-filters.vue';
import pagination from '@webapp/components/pagination/pagination';

// Mixins
import transactionMixin from '@webapp/helpers/transaction-mixin.js';

// Utils
import MediaQueryListener from '@webapp/utils/media-query-listener.ts';

export default {
    name: 'wallet-transactions',
    components: {
        toggleOptions,
        partnerLogo,
        walletTransactionDetails,
        loadingSpinner,
        backButton,
        transactionsFilters,
        pagination,
    },
    mixins: [transactionMixin],
    props: {
        selectedTokenCode: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            showLoader: false,
            currentTransactionType: TransactionType.Main,
            openedTransactionIndex: '',
            savedFiltersRequest: '',
            unsubscribeMediaQueryListener: null,
            areFiltersVisible: false,
            isInitialLoad: true,
        };
    },
    computed: {
        ...mapGetters(['isMobileDevice', 'partnerAppEmbedCustomization']),

        ...mapGetters('partners', ['partnerById', 'partnerByCurrencyId']),

        ...mapGetters('transactions', [
            'formattedTransactionsList',
            'transactionsPagination',
        ]),

        ...mapGetters('wallet', ['isChzAllowed']),

        transactions() {
            return this.formattedTransactionsList(this.currentTransactionType);
        },

        toggleOptions() {
            return [
                {
                    name: this.isChzAllowed ? this.$t('transactionHistory.chzTransactions') : 'USD',
                    isSelected: true,
                    isDisabled: false,
                    type: TransactionType.Main,
                },
                {
                    name: this.$t('transactionHistory.tokenTransactions'),
                    isSelected: false,
                    isDisabled: false,
                    type: TransactionType.FanToken,
                },
            ];
        },
    },
    watch: {
        areFiltersVisible: {
            immediate: true,
            async handler(areVisible) {
                if (areVisible) {
                    return;
                }

                if (!this.isFiltersVisible) {
                    this.showLoader = true;

                    await this.fetchTransactions({
                        type: this.currentTransactionType,
                        limit: 50,
                    });

                    this.showLoader = false;
                }
            },
        },
    },
    mounted() {
        this.showLoader = true;

        this.unsubscribeMediaQueryListener = MediaQueryListener('(min-width: 1200px)', (matches) => {
            this.areFiltersVisible = matches;
        });
    },
    destroyed() {
        this.unsubscribeMediaQueryListener();
    },
    methods: {
        ...mapActions('transactions', ['fetchTransactions']),

        async fetchFilteredTransactions({
            type = null,
            page = 1,
            ...filtersRequest
        }) {
            this.currentTransactionType = type ?? TransactionType.Main;

            await this.fetchTransactions({
                type: this.currentTransactionType,
                limit: 50,
                page,
                filtersRequest,
            });

            this.showLoader = false;
        },

        onPaginationUpdate(page) {
            // To avoid the double fetch on load
            if (this.isInitialLoad) {
                this.isInitialLoad = false;

                return;
            }

            this.showLoader = true;

            this.fetchFilteredTransactions({
                ...this.savedFiltersRequest,
                page,
            });
        },

        onFiltersRequest(filters) {
            this.savedFiltersRequest = filters;
            this.showLoader = true;
            this.fetchFilteredTransactions(filters);
        },

        async afterTransactionDetailsEnter() {
            const parentEl = this.$refs.activeTransaction[0];

            await this.$nextTick();

            parentEl.scrollIntoView();
        },

        async setActive(items) {
            this.showLoader = true;
            this.openedTransactionIndex = '';
            const item = items.find((item) => item.isSelected);

            this.currentTransactionType = item.type;

            await this.fetchTransactions({
                type: this.currentTransactionType,
                limit: 50,
            });

            this.showLoader = false;
        },

        makeActive(index) {
            this.openedTransactionIndex = index === this.openedTransactionIndex ? '' : index;
        },
    },
};
</script>

<style lang="scss" scoped>
.filters {
    margin: 4rem 0 2rem 0;
}

.transactions {
    background: $white-color;
    padding: 1.8rem 2.8rem;
    border-radius: 1rem;

    @include max-width(sm) {
        padding: 1.8rem 0;
    }

    &__empty {
        font-size: 1.4rem;
        text-align: center;
        margin-top: 5rem;
    }

    &__pagination {
        padding: 3.7rem 2rem 0 2rem;
    }
}

.transaction {
    border-bottom: 0.1rem solid rgba($black-color, 0.1);

    &:last-child {
        border: none;
    }

    &:hover,
    &--active {
        background: linear-gradient(180deg, rgba($blue-lagoon-color, 0.05) 0%, rgba($cyprus-color, 0.05) 100%);
    }

    &--active {
        border: none;

        .icon-chevron {
            transform: rotate(0deg);
            align-self: flex-end;
            position: relative;
            top: -1rem;

            @include max-width(sm) {
                top: 0;
            }

            @include min-width(lg) {
                top: -2rem;
            }
        }
    }

    &__data {
        display: flex;
        justify-content: space-between;
        padding: 2.4rem 4.4rem 2.4rem 1.7rem;

        @include max-width(sm) {
            padding: 2.4rem 1rem;
        }

        &:hover {
            cursor: pointer;
        }

        &-right,
        &-left {
            display: flex;
            padding-top: 0.2rem;
        }

        &-left {
            i {
                font-size: 4.8rem;
                align-self: center;
            }
        }

        &-right {
            flex-direction: row;
            white-space: nowrap;

            @include max-width(sm) {
                flex-direction: column;
            }

            i {
                margin-top: 2rem;
                align-self: center;
                transform: rotate(180deg);
                color: $cyprus-color;

                @include max-width(sm) {
                    align-self: initial;
                    margin-right: 2rem;
                }

                @include min-width (lg) {
                    margin-top: 0;
                }
            }
        }
    }

    &__amount {
        font-size: 1.5rem;
        line-height: 2rem;
        font-weight: 600;
        padding-right: 0;

        @include min-width(lg) {
            padding-right: 12rem;
            padding-bottom: 0;
        }

        @include max-width(sm) {
            padding-bottom: 0;
        }

        &--amber {
            color: $pizazz-color;
        }

        &--green {
            color: $lima-color;
        }

        &--red {
            color: $monza-color;
        }
    }

    &__name {
        max-width: 18rem;
        font-size: 1.5rem;
        font-weight: 700;
        line-height: 1.8rem;
        padding-left: 2rem;

        @include max-width(sm) {
            max-width: 9rem;
            font-size: 1.2rem;
        }

        &--time {
            display: block;
            font-size: 1.2rem;
            line-height: 1.8rem;
            color: rgba($cyprus-color, 0.5);
            padding-top: 0.5rem;

            @include max-width(sm) {
                font-size: 1rem;
            }
        }
    }

    &__heading {
        background: $athens-gray-color;
        padding: 2.6rem 1.5rem 1.5rem;

        .heading {
            &__title,
            &__date {
                font-size: 1.2rem;
                line-height: 1.8rem;
                text-transform: uppercase;
                display: block;
            }

            &__title {
                font-weight: 600;
                line-height: 3rem;
            }
        }
    }

    &__details {
        padding: 0 4.5rem 3rem;

        @include max-width(sm) {
            padding: 0 0 3rem;
        }

        @include min-width(xl) {
            padding: 0 7.5rem 3rem 9.5rem;
        }
    }
}

.wrapper {
    padding-bottom: 9rem;
    min-height: 50vh;
}

.logo,
.placeholder {
    width: 4.8rem;
    height: 4.8rem;
    align-self: center;
}

.placeholder {
    border-radius: 50%;
    background-color: $silver-color;
}
</style>